import React, { useState, useEffect } from "react";
import service from "../../services";
import dayjs from "dayjs";
import { Box, Grid, Typography as MuiTypography, Card, CardContent } from "@mui/material";
import DashboardLayout from "../../layout/DashboardLayout/DashboardLayout";
import {
  Table,
  Input,
  Button,
  DatePicker,
  Space,
  ConfigProvider,
  Row,
  Col,
  Avatar,
  Typography,
  Popover,
  Select
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from "@ant-design/icons";
import "./styles.css";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const UserChallengeActivity = () => {
  const defaultStats = [
    {
      title: "Total Active Users",
      value: "0",
      change: "0%",
      isUp: true,
      chartColor: "#12B76A"
    },
    {
      title: "Users Joined Challenges",
      value: "0",
      change: "0%",
      isUp: true,
      chartColor: "#12B76A"
    },
    {
      title: "User Drop Challenge Count",
      value: "0",
      change: "0%",
      isUp: false,
      chartColor: "#12B76A"
    }
  ];

  const [loading, setLoading] = useState(false);
  const [statsData, setStatsData] = useState(defaultStats);
  const [tableData, setTableData] = useState([]);

  // Default date range (none)
  const [dateRange, setDateRange] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let startDate = "";
      let endDate = "";

      if (dateRange && dateRange[0] && dateRange[1]) {
        startDate = dateRange[0].format("YYYY-MM-DD");
        endDate = dateRange[1].format("YYYY-MM-DD");
      }

      // Fetch Table Data with date range
      const detailsRes = await service.getUserActivityDetails(1, 1000, startDate, endDate);
      const rawData = detailsRes.data?.data?.userChallengeDetails;

      const mappedTableData = Array.isArray(rawData) ? rawData.map((item, index) => ({
        key: item.userChallengeId || index,
        user: {
          name: item.userName,
          email: item.userEmail,
          avatar: item.userProfilePicture,
          initial: item.userName ? item.userName.charAt(0).toUpperCase() : '?'
        },
        challengeId: `#${item.challengeId}`,
        challengeName: item.challengeName,
        type: capitalizeFirstLetter(item.challengeType),
        co2ToSave: `${item.co2ToSave} kg`,
        mode: capitalizeFirstLetter(item.mode),
        frequency: item.frequency,
        startedOn: item.startedOn,
        completedOn: item.completedOn || '-',
        status: mapStatus(item.status)
      })) : [];

      setTableData(mappedTableData);

      // Fetch Stats Data with date range
      try {
        const statsRes = await service.getUserActivityStats(startDate, endDate);
        const mappedStats = mapStatsData(statsRes.data?.data || {});
        setStatsData(mappedStats);
      } catch (err) {
        console.error("Error fetching activity stats:", err);
      }
    } catch (error) {
      console.error("Error fetching user activity data:", error);
    } finally {
      setLoading(false);
    }
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const mapStatus = (apiStatus) => {
    // Map API UPPERCASE status to UI Title Case status
    switch (apiStatus) {
      case 'IN_PROGRESS': return 'In progress';
      case 'COMPLETED': return 'Completed';
      case 'TODO': return 'To-do'; // Guessing API value
      case 'IN_QUEUE': return 'In Queue'; // Guessing API value
      default: return capitalizeFirstLetter(apiStatus).replace('_', ' ');
    }
  };

  const mapStatsData = (apiStats) => {
    // Map API response to UI format
    const activeUsersPct = apiStats.noOfPercentageUsersIncreased || 0;
    const joinedPct = apiStats.noOfPercentageUsersJoinedChallenges || 0;
    const droppedPct = apiStats.noOfPercentageUsersDroppedChallenges || 0;

    return [
      {
        title: "Total Active Users",
        value: apiStats.noOfActiveUsers !== undefined ? apiStats.noOfActiveUsers : "0",
        change: `${Math.abs(activeUsersPct)}%`,
        isUp: activeUsersPct >= 0,
        chartColor: activeUsersPct >= 0 ? "#12B76A" : "#F04438"
      },
      {
        title: "Users Joined Challenges",
        value: apiStats.usersJoinedChallenges !== undefined ? apiStats.usersJoinedChallenges : "0",
        change: `${Math.abs(joinedPct)}%`,
        isUp: joinedPct >= 0,
        chartColor: joinedPct >= 0 ? "#12B76A" : "#F04438"
      },
      {
        title: "User Drop Challenge Count",
        value: apiStats.noOfUsersThatDroppedChallenges !== undefined ? apiStats.noOfUsersThatDroppedChallenges : "0",
        change: `${Math.abs(droppedPct)}%`,
        isUp: droppedPct > 0,
        chartColor: droppedPct > 0 ? "#12B76A" : "#F04438"
      }
    ];
  };



  useEffect(() => {
    fetchData();
  }, [dateRange]); // Refetch when date range changes

  const getTypeStyle = (type) => {
    switch (type) {
      case 'Monthly': return { color: '#6941C6', bg: '#F9F5FF' };
      case 'Daily': return { color: '#175CD3', bg: '#EFF8FF' };
      case 'Weekly': return { color: '#C11574', bg: '#FDF2FA' };
      default: return { color: '#344054', bg: '#F2F4F7' };
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'In progress': return { color: '#175CD3', bg: '#EFF8FF' };
      case 'To-do': return { color: '#344054', bg: '#F2F4F7' };
      case 'In Queue': return { color: '#B54708', bg: '#FFFAEB' }; // Orange
      case 'Completed': return { color: '#067647', bg: '#ECFDF3' }; // Green
      default: return { color: '#344054', bg: '#F2F4F7' };
    }
  };

  const columns = [
    {
      title: "User Name",
      dataIndex: "user",
      key: "user",
      width: 250,
      render: (user) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar src={user.avatar} style={{ backgroundColor: user.initial ? '#D1FADF' : undefined, color: '#000' }}>
            {user.initial}
          </Avatar>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text strong style={{ fontSize: '14px', color: '#101828' }}>{user.name}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>{user.email}</Text>
          </div>
        </div>
      )
    },
    {
      title: "Challenge ID",
      dataIndex: "challengeId",
      key: "challengeId",
      width: 120,
    },
    {
      title: "Challenge Name",
      dataIndex: "challengeName",
      key: "challengeName",
      width: 250,
      render: (text) => <Text style={{ color: '#475467' }}>{text}</Text>
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (text) => {
        const style = getTypeStyle(text);
        return (
          <span style={{
            color: style.color,
            backgroundColor: style.bg,
            padding: '2px 10px',
            borderRadius: '16px',
            fontWeight: 500,
            display: 'inline-block',
            fontSize: '12px'
          }}>
            {text}
          </span>
        );
      }
    },
    {
      title: "Co2 to Save",
      dataIndex: "co2ToSave",
      key: "co2ToSave",
      width: 120,
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      width: 150,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      key: "frequency",
      width: 100,
    },
    {
      title: "Started On",
      dataIndex: "startedOn",
      key: "startedOn",
      width: 150,
    },
    {
      title: "Completed On",
      dataIndex: "completedOn",
      key: "completedOn",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (text) => {
        const style = getStatusStyle(text);
        return (
          <span style={{
            color: style.color,
            backgroundColor: style.bg,
            padding: '2px 10px',
            borderRadius: '16px',
            fontWeight: 500,
            display: 'inline-block',
            fontSize: '12px'
          }}>
            {text}
          </span>
        );
      }
    },
  ];

  const [searchText, setSearchText] = useState("");
  // const [dateRange, setDateRange] = useState(null); // Removed locally defined dateRange, using state one
  const [filteredData, setFilteredData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Filter States
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    challengeName: undefined,
    tripId: "",
    mode: undefined,
    type: undefined,
    status: undefined,
    dateRange: null
  });

  // Temporary state for the popover inputs
  const [tempFilters, setTempFilters] = useState(filters);

  // Handle opening/closing popover
  const handleOpenChange = (newOpen) => {
    setFilterOpen(newOpen);
    if (newOpen) {
      setTempFilters(filters); // Reset temp to current applied filters on open
    }
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    const cleared = {
      challengeName: undefined,
      tripId: "",
      mode: undefined,
      type: undefined,
      status: undefined,
      dateRange: null
    };
    setTempFilters(cleared);
    setFilters(cleared);
  };

  // Filter Data Effect
  useEffect(() => {
    let result = Array.isArray(tableData) ? tableData : [];

    // 1. Global Search (Search Bar)
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      result = result.filter(item =>
        (item.user?.name || "").toLowerCase().includes(lowerSearch) ||
        (item.challengeName || "").toLowerCase().includes(lowerSearch) ||
        (item.challengeId || "").toLowerCase().includes(lowerSearch)
      );
      // Prioritize exact Challenge ID matches, then prefix matches, then partial matches
      result = result.sort((a, b) => {
        const idA = (a.challengeId || "").toLowerCase();
        const idB = (b.challengeId || "").toLowerCase();
        const exactA = idA === lowerSearch || idA === `#${lowerSearch}`;
        const exactB = idB === lowerSearch || idB === `#${lowerSearch}`;
        if (exactA !== exactB) return exactA ? -1 : 1;
        const prefixA = idA.startsWith(lowerSearch) || idA.startsWith(`#${lowerSearch}`);
        const prefixB = idB.startsWith(lowerSearch) || idB.startsWith(`#${lowerSearch}`);
        if (prefixA !== prefixB) return prefixA ? -1 : 1;
        return 0;
      });
    }

    // 2. Head-level Date Range (if used independently or synced)
    // For now, let's say the header date picker updates the MAIN date range state mapStatsData uses,
    // and the filter dropdown might have its OWN effective date or is same.
    // The design shows "Effective from & to" inside the filter.
    // Let's assume the header date picker is the primary one for now, as implemented before.
    // If the filter dropdown's date range is distinct, we process it here.

    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const start = filters.dateRange[0];
      const end = filters.dateRange[1];
      result = result.filter(item => {
        const dateStr = item.startedOn;
        if (!dateStr) return false;
        const dateObj = new Date(dateStr);
        if (isNaN(dateObj)) return true;
        return dateObj >= start.toDate() && dateObj <= end.toDate();
      });
    } else if (dateRange && dateRange[0] && dateRange[1]) {
      const start = dateRange[0];
      const end = dateRange[1];
      result = result.filter(item => {
        const dateStr = item.startedOn;
        if (!dateStr) return false;
        const dateObj = new Date(dateStr);
        if (isNaN(dateObj)) return true;
        return dateObj >= start.toDate() && dateObj <= end.toDate();
      });
    }

    // 3. Advanced Filters
    if (filters.challengeName && filters.challengeName !== 'All') {
      result = result.filter(item => item.challengeName === filters.challengeName);
    }
    if (filters.tripId) {
      const lowerTripId = filters.tripId.toLowerCase();
      result = result.filter(item => item.challengeId.toLowerCase().includes(lowerTripId));
      // Prioritize exact matches, then prefix matches, then partial matches
      result = result.sort((a, b) => {
        const idA = (a.challengeId || "").toLowerCase();
        const idB = (b.challengeId || "").toLowerCase();
        const exactA = idA === lowerTripId || idA === `#${lowerTripId}`;
        const exactB = idB === lowerTripId || idB === `#${lowerTripId}`;
        if (exactA !== exactB) return exactA ? -1 : 1;
        const prefixA = idA.startsWith(lowerTripId) || idA.startsWith(`#${lowerTripId}`);
        const prefixB = idB.startsWith(lowerTripId) || idB.startsWith(`#${lowerTripId}`);
        if (prefixA !== prefixB) return prefixA ? -1 : 1;
        return 0;
      });
    }
    if (filters.mode && filters.mode !== 'All') {
      result = result.filter(item => item.mode === filters.mode);
    }
    if (filters.type && filters.type !== 'All') {
      // UI type is "Daily", API/Table type is "Daily" (capitalized)
      result = result.filter(item => item.type === filters.type);
    }
    if (filters.status && filters.status !== 'All') {
      // Table status is "In progress", "Completed" etc.
      // Filter status might be "Active", "Inactive". Need mapping or exact match?
      // Let's assume exact match from options for now.
      result = result.filter(item => item.status === filters.status);
    }


    setFilteredData(result);
    setPagination((prev) => ({ ...prev, current: 1 }));

  }, [searchText, tableData, dateRange, filters]);


  // Get unique lists for dropdowns from current data
  const challengeNames = [...new Set(tableData.map(item => item.challengeName))];
  // const modes = [...new Set(tableData.map(item => item.mode))]; // Or use presets

  const filterContent = (
    <div style={{ width: 600, padding: 8 }}>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Text strong>Challenge Name</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="All"
            allowClear
            value={tempFilters.challengeName}
            onChange={(val) => setTempFilters({ ...tempFilters, challengeName: val })}
          >
            {challengeNames.map(name => <Select.Option key={name} value={name}>{name}</Select.Option>)}
          </Select>
        </Col>
        <Col span={12}>
          <Text strong>Trip ID/Challenge ID</Text>
          <Input
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Search by ID"
            value={tempFilters.tripId}
            onChange={(e) => setTempFilters({ ...tempFilters, tripId: e.target.value })}
          />
        </Col>
        <Col span={12}>
          <Text strong>Mode</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="All"
            allowClear
            value={tempFilters.mode}
            onChange={(val) => setTempFilters({ ...tempFilters, mode: val })}
          >
            <Select.Option value="Bicycle">Bicycle</Select.Option>
            <Select.Option value="Walk">Walk</Select.Option>
            <Select.Option value="Bus">Bus</Select.Option>
            <Select.Option value="Train">Train</Select.Option>
            <Select.Option value="Car">Car</Select.Option>
          </Select>
        </Col>
        <Col span={12}>
          <Text strong>Type</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="All"
            allowClear
            value={tempFilters.type}
            onChange={(val) => setTempFilters({ ...tempFilters, type: val })}
          >
            <Select.Option value="Daily">Daily</Select.Option>
            <Select.Option value="Weekly">Weekly</Select.Option>
            <Select.Option value="Monthly">Monthly</Select.Option>
          </Select>
        </Col>
        <Col span={12}>
          <Text strong>Effective from & to</Text>
          <RangePicker
            style={{ width: '100%', marginTop: 8 }}
            value={tempFilters.dateRange}
            onChange={(dates) => setTempFilters({ ...tempFilters, dateRange: dates })}
          />
        </Col>
        <Col span={12}>
          <Text strong>Status</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="All"
            allowClear
            value={tempFilters.status}
            onChange={(val) => setTempFilters({ ...tempFilters, status: val })}
          >
            <Select.Option value="In progress">In progress</Select.Option>
            <Select.Option value="Completed">Completed</Select.Option>
            <Select.Option value="To-do">To-do</Select.Option>
            <Select.Option value="In Queue">In Queue</Select.Option>
          </Select>
        </Col>
      </Row>
      <Row justify="end" style={{ marginTop: 24, gap: 12 }}>
        <Button onClick={handleClearFilters}>Clear</Button>
        <Button type="primary" onClick={handleApplyFilters} style={{ background: '#5FB92A' }}>Apply</Button>
      </Row>
    </div>
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#5FB92A",
          fontFamily: "'Manrope', sans-serif",
          borderRadius: 8,
        },
      }}
    >
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Header */}
          <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
            <Col>
              <Title level={3} style={{ margin: 0, fontWeight: 600 }}>User Challenge Activity</Title>
              <Text type="secondary">View users challenge activity .</Text>
            </Col>
            <Col>
              <RangePicker
                size="large"
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
              />
            </Col>
          </Row>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {statsData.map((stat, index) => (
              <Grid item xs={12} md={4} key={index} sx={{ display: 'flex' }}>
                <Card sx={{ boxShadow: 'none', border: '1px solid #EAECF0', borderRadius: '12px', width: '100%' }}>
                  <CardContent>
                    <MuiTypography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                      {stat.title}
                    </MuiTypography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <Box>
                        <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                          {stat.value}
                        </MuiTypography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                          {stat.isUp ?
                            <ArrowUpOutlined style={{ color: stat.chartColor, flexShrink: 0 }} /> :
                            <ArrowDownOutlined style={{ color: stat.chartColor, flexShrink: 0 }} />
                          }
                          <MuiTypography variant="body2" sx={{ color: stat.chartColor, fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {stat.change}
                          </MuiTypography>
                          <MuiTypography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            vs last month
                          </MuiTypography>
                        </Box>
                      </Box>
                      {/* Simple Chart Visualization */}
                      <Box sx={{ width: '100px', height: '40px' }}>
                        <svg width="100%" height="100%" viewBox="0 0 100 40" preserveAspectRatio="none">
                          {/* Chart line */}
                          <path d={stat.isUp ? "M0 35 Q 20 35 30 20 T 60 25 T 100 5 L 100 40 L 0 40 Z" : "M0 5 Q 20 5 40 20 T 70 25 T 100 35 L 100 40 L 0 40 Z"}
                            fill="none"
                            stroke={stat.chartColor}
                            strokeWidth="2"
                          />
                          {/* Fill area - dynamic based on isUp */}
                          <path d={stat.isUp ? "M0 35 Q 20 35 30 20 T 60 25 T 100 5 L 100 40 L 0 40 Z" : "M0 5 Q 20 5 40 20 T 70 25 T 100 35 L 100 40 L 0 40 Z"}
                            fill={`url(#gradient-${index})`}
                            stroke="none"
                            opacity="0.1"
                          />
                          <defs>
                            <linearGradient id={`gradient-${index}`} x1="0" x2="0" y1="0" y2="1">
                              <stop offset="0%" stopColor={stat.chartColor} />
                              <stop offset="100%" stopColor="white" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                        </svg>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Table Section */}
          <div className="user-challenges-pagination-wrapper" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #EAECF0', padding: '16px' }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }} align="middle">
              <Col flex="1">
                <Input
                  size="large"
                  placeholder="Search for Users ,Challenges"
                  prefix={<SearchOutlined style={{ color: '#667085' }} />}
                  style={{ maxWidth: '400px' }}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Col>
              <Col>
                <Popover
                  content={filterContent}
                  title="Filter User Activity"
                  trigger="click"
                  open={filterOpen}
                  onOpenChange={handleOpenChange}
                  placement="bottomRight"
                >
                  <Button icon={<FilterOutlined />} size="large">Filters</Button>
                </Popover>
              </Col>
            </Row>

            {loading && (
              <div className="table-progress-bar">
                <div className="table-progress-bar-inner" />
              </div>
            )}
            <Table
              columns={columns}
              dataSource={filteredData}
              loading={false}
              pagination={{
                position: ["bottomCenter"],
                total: filteredData.length,
                current: pagination.current,
                pageSize: pagination.pageSize,
                showSizeChanger: false,
                onChange: (page, pageSize) => {
                  setPagination({ current: page, pageSize });
                },
                itemRender: (current, type, originalElement) => {
                  const totalPages = Math.ceil(filteredData.length / pagination.pageSize);
                  if (type === 'prev') {
                    if (totalPages <= 1) return null;
                    const isFirstPage = pagination.current <= 1;
                    return (
                      <Button
                        className="custom-pagination-link"
                        disabled={isFirstPage}
                      >
                        ← Previous
                      </Button>
                    );
                  }
                  if (type === 'next') {
                    if (totalPages <= 1) return null;
                    const isLastPage = pagination.current >= totalPages;
                    return (
                      <Button
                        className="custom-pagination-link"
                        disabled={isLastPage}
                      >
                        Next →
                      </Button>
                    );
                  }
                  return originalElement;
                },
              }}
              scroll={{ x: 'max-content' }}
            />
          </div>
        </Box>
      </DashboardLayout>
    </ConfigProvider>
  );
};

export default UserChallengeActivity;
